<template>
  <div class="min-h-screen bg-zinc-900 flex items-center justify-center p-1 sm:p-4 font-nunito">
    <!-- Container principal -->
    <div class="w-full max-w-lg">
      <!-- Logo -->
      <div class="flex items-center justify-center mb-8">
        <img src="@/assets/bot_express_logo.png" alt="Logo" class="h-auto w-[180px] sm:w-[280px]">
      </div>

      <!-- Card de 2FA -->
      <div class="bg-white/5 rounded-xl overflow-hidden">
        <div class="p-6 sm:p-8">
          <!-- Cabeçalho -->
          <div class="mb-8">
            <h2 class="text-white text-2xl font-bold mb-2">Verificação em Duas Etapas</h2>
            <p class="text-[#888888]">Insira o código de 6 dígitos do seu App de 2FA</p>
          </div>

          <!-- Instructions -->
          <div class="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
            <div class="flex items-start gap-3">
              <svg class="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p class="text-sm text-[#AAAAAA]">
                  Abra o <strong class="text-white">App de 2FA</strong> no seu celular e digite
                  o código de 6 dígitos exibido.
                </p>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div class="flex items-center gap-2 text-red-400">
              <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm font-medium">{{ errorMessage }}</span>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6">
            <div class="flex items-center gap-2 text-emerald-400">
              <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span class="text-sm font-medium">{{ successMessage }}</span>
            </div>
          </div>

          <!-- 6-Digit Code Input -->
          <div class="space-y-4 mb-6">
            <div class="text-center">
              <label class="text-sm font-medium text-[#AAAAAA]">Código de verificação</label>
            </div>

            <!-- Inputs de código (6 dígitos) -->
            <div class="flex justify-center gap-2 sm:gap-3">
              <input 
                v-for="(digit, index) in 6" 
                :key="index" 
                :ref="(el) => { digitInputs[index] = el as HTMLInputElement | null }" 
                v-model="code[index]" 
                type="text" 
                maxlength="1" 
                inputmode="numeric" 
                pattern="[0-9]*"
                @input="handleCodeInput(index, $event)" 
                @keydown="handleKeyDown(index, $event)"
                @paste="handlePaste" 
                @focus="handleFocus(index)" 
                :disabled="isLoading"
                class="w-12 h-14 text-center text-2xl font-semibold bg-white/5 border-2 border-white/10 rounded-lg text-white focus:outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                :class="{
                  'border-red-500': errorMessage,
                  'border-emerald-500': successMessage,
                  'animate-pulse': isLoading
                }" 
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="space-y-4">
            <!-- Verify Button -->
            <button 
              @click="handleVerification" 
              :disabled="isLoading || code.join('').length !== 6"
              class="w-full py-3 px-4 bg-[#10B981] hover:bg-[#0EA271] text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin mr-2" />
              <span>{{ isLoading ? 'Verificando...' : 'Verificar Código' }}</span>
            </button>

            <!-- Back to Login -->
            <div class="text-center pt-2">
              <button 
                @click="logout" 
                :disabled="isLoading"
                class="text-sm text-[#888888] hover:text-[#AAAAAA] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Voltar para o login
              </button>
            </div>
          </div>

          <!-- Help Section -->
          <div class="mt-8 pt-6 border-t border-white/10">
            <div class="flex items-start gap-2">
              <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.348 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p class="text-xs text-[#666666]">
                Não consegue validar o código de 2 fatores? Entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toaster />
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { Loader2 } from 'lucide-vue-next'
import { Toaster } from '@/components/ui/toast'
import type { AxiosError } from 'axios'

const { validate2FA, logout: authLogout } = useAuth()
const router = useRouter()

// Refs
const digitInputs = ref<(HTMLInputElement | null)[]>([])
const code = reactive<string[]>(Array(6).fill(''))
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const handleCodeInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  // Permitir apenas números
  if (!/^\d?$/.test(value)) {
    input.value = ''
    code[index] = ''
    return
  }

  code[index] = value

  // Mover para o próximo input se digitou um número
  if (value && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }

  // Limpar mensagens de erro ao digitar
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

const handleKeyDown = (index: number, event: KeyboardEvent) => {
  const key = event.key

  // Se pressionar backspace e o campo estiver vazio, voltar para o anterior
  if (key === 'Backspace' && !code[index] && index > 0) {
    code[index] = ''
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se pressionar delete, limpar o campo atual
  if (key === 'Delete') {
    code[index] = ''
  }

  // Se pressionar seta para esquerda
  if (key === 'ArrowLeft' && index > 0) {
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se pressionar seta para direita
  if (key === 'ArrowRight' && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }

  // Permitir colar
  if ((event.ctrlKey || event.metaKey) && key === 'v') {
    return // Permitir o comportamento padrão de colar
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const pastedData = event.clipboardData?.getData('text') || ''
  const numbers = pastedData.replace(/\D/g, '').slice(0, 6)

  // Preencher os campos com os números colados
  for (let i = 0; i < 6; i++) {
    code[i] = numbers[i] || ''
  }

  // Focar no último campo preenchido ou no último campo
  const lastFilledIndex = Math.min(numbers.length, 5)
  setTimeout(() => {
    digitInputs.value[lastFilledIndex]?.focus()
  }, 10)
}

const handleFocus = (index: number) => {
  // Selecionar todo o texto quando focar
  setTimeout(() => {
    digitInputs.value[index]?.select()
  }, 10)
}

const handleVerification = async () => {
  errorMessage.value = ''
  successMessage.value = ''
  isLoading.value = true

  try {
    const response = await validate2FA(code.join(''))

    if (response.token) {
      successMessage.value = 'Código verificado com sucesso!'
      
      setTimeout(() => {
        router.push({ name: 'organizations' })
      }, 500)
    }
  } catch (error) {
    const err = error as AxiosError<{ errors?: { message: string }[], message?: string }>
    const message = err.response?.data?.errors?.map((error) => error.message).join(', ') 
      || err.response?.data?.message 
      || 'Código inválido. Tente novamente.'
    
    errorMessage.value = message
  } finally {
    isLoading.value = false
  }
}

const logout = () => {
  authLogout()
  router.push({ name: 'login' })
}

// Lifecycle
onMounted(() => {
  // Auto-focus no primeiro input
  setTimeout(() => {
    digitInputs.value[0]?.focus()
  }, 100)
})
</script>

<style scoped>
/* Pulse animation for loading */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Hide number input arrows */
input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
